/**
 * OnboardingChecklist — dashboard widget showing 6-step setup progress.
 * Fetches /onboarding/progress; hides itself when all 6 steps done OR user dismisses.
 * Dismissal persisted in localStorage keyed by shop_id so it doesn't bother on every visit.
 */
import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, X, ArrowRight, Sparkles } from "lucide-react";

const DISMISS_KEY = (shopId) => `hp_onboarding_dismissed_${shopId}`;

export default function OnboardingChecklist() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user?.shop_id) return;
    setDismissed(localStorage.getItem(DISMISS_KEY(user.shop_id)) === "1");
    api.get("/onboarding/progress").then((r) => setData(r.data)).catch(() => {});
  }, [user?.shop_id]);

  if (!data || dismissed || data.all_done) return null;

  const pct = Math.round((data.completed / data.total) * 100);
  const dismiss = () => {
    if (user?.shop_id) localStorage.setItem(DISMISS_KEY(user.shop_id), "1");
    setDismissed(true);
  };

  return (
    <div className="bg-gradient-to-br from-emerald-50 to-sky-50 border border-emerald-200 rounded-md p-5" data-testid="onboarding-checklist">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2 font-display font-bold text-lg text-emerald-900">
            <Sparkles className="w-5 h-5 text-emerald-600" /> Welcome to HisaabPro!
          </div>
          <p className="text-sm text-emerald-800/80">Apna installment business in a few simple steps set up karein.</p>
        </div>
        <button onClick={dismiss} className="text-emerald-700/60 hover:text-emerald-900 p-1" title="Dismiss" data-testid="onboarding-dismiss">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="mb-4">
        <div className="flex items-center justify-between text-xs font-bold text-emerald-800 mb-1">
          <span>Progress: {data.completed} of {data.total} done</span>
          <span className="tabular">{pct}%</span>
        </div>
        <div className="h-2 w-full bg-emerald-100 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {data.steps.map((s) => (
          <Link
            to={s.link}
            key={s.key}
            className={`flex items-center gap-2.5 rounded-md border px-3 py-2.5 text-sm transition ${
              s.done
                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                : "bg-white border-slate-200 hover:border-emerald-400 hover:shadow-sm"
            }`}
            data-testid={`onboarding-step-${s.key}`}
          >
            {s.done ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            ) : (
              <Circle className="w-4 h-4 text-slate-400 shrink-0" />
            )}
            <span className={`flex-1 ${s.done ? "line-through opacity-70" : "font-medium"}`}>{s.label}</span>
            {!s.done && <ArrowRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
          </Link>
        ))}
      </div>
    </div>
  );
}
